import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Link,
  Copy,
  Check,
  Globe,
  Share2,
  QrCode,
  ExternalLink,
  AlertCircle,
} from "lucide-react";

interface StoreData {
  name: string;
  description: string;
  logo: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  items: any[];
  isPublished: boolean;
  storeUrl: string;
  contactInfo?: {
    email: string;
    phone: string;
    location: string;
    businessHours: {
      weekdays: string;
      saturday: string;
      sunday: string;
    };
  };
}

interface StoreLinkGeneratorProps {
  storeData: StoreData;
  onStoreUpdate: (updates: Partial<StoreData>) => void;
}

const StoreLinkGenerator = ({
  storeData,
  onStoreUpdate,
}: StoreLinkGeneratorProps) => {
  const [customSlug, setCustomSlug] = useState("");
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const generateStoreUrl = () => {
    if (!storeData.name.trim()) {
      alert("Please set your store name first");
      return;
    }

    setIsGenerating(true);

    // Simulate API call
    setTimeout(() => {
      const slug =
        customSlug || storeData.name.toLowerCase().replace(/[^a-z0-9]/g, "-");

      // Use environment variable or fallback to localhost for development
      const baseUrl =
        import.meta.env.VITE_STORE_BASE_URL || window.location.origin;
      const storeUrl = `${baseUrl}/preview?name=${encodeURIComponent(
        storeData.name
      )}&primaryColor=${encodeURIComponent(
        storeData.primaryColor
      )}&secondaryColor=${encodeURIComponent(
        storeData.secondaryColor
      )}&accentColor=${encodeURIComponent(
        storeData.accentColor
      )}&logo=${encodeURIComponent(
        storeData.logo
      )}&slug=${slug}&email=${encodeURIComponent(
        storeData.contactInfo?.email || ""
      )}&phone=${encodeURIComponent(
        storeData.contactInfo?.phone || ""
      )}&location=${encodeURIComponent(
        storeData.contactInfo?.location || ""
      )}&weekdays=${encodeURIComponent(
        storeData.contactInfo?.businessHours?.weekdays || ""
      )}&saturday=${encodeURIComponent(
        storeData.contactInfo?.businessHours?.saturday || ""
      )}&sunday=${encodeURIComponent(
        storeData.contactInfo?.businessHours?.sunday || ""
      )}`;

      onStoreUpdate({
        storeUrl,
        isPublished: true,
      });

      setIsGenerating(false);
    }, 2000);
  };

  const copyToClipboard = async () => {
    if (storeData.storeUrl) {
      try {
        await navigator.clipboard.writeText(storeData.storeUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy: ", err);
      }
    }
  };

  const shareStore = async () => {
    if (navigator.share && storeData.storeUrl) {
      try {
        await navigator.share({
          title: storeData.name,
          text: storeData.description,
          url: storeData.storeUrl,
        });
      } catch (err) {
        console.error("Error sharing: ", err);
      }
    } else {
      // Fallback to copying
      copyToClipboard();
    }
  };

  const isStoreReady =
    storeData.name &&
    storeData.items.length > 0 &&
    storeData.contactInfo?.email;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-xl font-bold">Publish Your Store</h3>
        <p className="text-muted-foreground">
          Generate a unique link to share your Kente store with customers
        </p>
      </div>

      {/* Store Readiness Check */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Store Readiness
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center ${
                  storeData.name
                    ? "bg-green-100 text-green-600"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                {storeData.name ? "✓" : "○"}
              </div>
              <span
                className={storeData.name ? "text-green-600" : "text-gray-500"}
              >
                Store name set
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center ${
                  storeData.items.length > 0
                    ? "bg-green-100 text-green-600"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                {storeData.items.length > 0 ? "✓" : "○"}
              </div>
              <span
                className={
                  storeData.items.length > 0
                    ? "text-green-600"
                    : "text-gray-500"
                }
              >
                {storeData.items.length} items added
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center ${
                  storeData.description
                    ? "bg-green-100 text-green-600"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                {storeData.description ? "✓" : "○"}
              </div>
              <span
                className={
                  storeData.description ? "text-green-600" : "text-gray-500"
                }
              >
                Store description added
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center ${
                  storeData.contactInfo?.email
                    ? "bg-green-100 text-green-600"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                {storeData.contactInfo?.email ? "✓" : "○"}
              </div>
              <span
                className={
                  storeData.contactInfo?.email
                    ? "text-green-600"
                    : "text-gray-500"
                }
              >
                Contact information added
              </span>
            </div>
          </div>

          {!isStoreReady && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Complete the above requirements before publishing your store.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* URL Generation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Store URL
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!storeData.storeUrl ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="custom-slug">Custom URL (Optional)</Label>
                <div className="flex gap-2">
                  <span className="px-3 py-2 bg-gray-100 rounded-l-md text-sm">
                    kente-stores.com/
                  </span>
                  <Input
                    id="custom-slug"
                    value={customSlug}
                    onChange={(e) => setCustomSlug(e.target.value)}
                    placeholder="my-kente-store"
                    className="rounded-l-none"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Leave empty to auto-generate from your store name
                </p>
              </div>

              <Button
                onClick={generateStoreUrl}
                disabled={!isStoreReady || isGenerating}
                className="w-full"
              >
                {isGenerating ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Generating Store...
                  </>
                ) : (
                  <>
                    <Link className="h-4 w-4 mr-2" />
                    Generate Store Link
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Your Store URL</Label>
                <div className="flex gap-2">
                  <Input
                    value={storeData.storeUrl}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyToClipboard}
                    className="px-3"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={shareStore}
                  className="flex-1"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share Store
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open(storeData.storeUrl, "_blank")}
                  className="flex-1"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Visit Store
                </Button>
              </div>

              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-green-800">
                    Your store is now live!
                  </span>
                </div>
                <p className="text-xs text-green-700 mt-1">
                  Customers can now visit your store and make purchases
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Store Statistics */}
      {storeData.storeUrl && (
        <Card>
          <CardHeader>
            <CardTitle>Store Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">
                  {storeData.items.length}
                </div>
                <div className="text-sm text-muted-foreground">Total Items</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">0</div>
                <div className="text-sm text-muted-foreground">Total Views</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">0</div>
                <div className="text-sm text-muted-foreground">
                  Total Orders
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">₵0</div>
                <div className="text-sm text-muted-foreground">
                  Total Revenue
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sharing Options */}
      {storeData.storeUrl && (
        <Card>
          <CardHeader>
            <CardTitle>Share Your Store</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Share your store with customers through these channels:
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Button variant="outline" size="sm" className="justify-start">
                  <span className="mr-2">📱</span>
                  WhatsApp
                </Button>
                <Button variant="outline" size="sm" className="justify-start">
                  <span className="mr-2">📧</span>
                  Email
                </Button>
                <Button variant="outline" size="sm" className="justify-start">
                  <span className="mr-2">📘</span>
                  Facebook
                </Button>
                <Button variant="outline" size="sm" className="justify-start">
                  <span className="mr-2">📷</span>
                  Instagram
                </Button>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <QrCode className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">
                    QR Code Available
                  </span>
                </div>
                <p className="text-xs text-blue-700">
                  Generate a QR code for easy sharing at events and markets
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StoreLinkGenerator;
